import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { IPayment } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { EmailReceiptModal } from './POS/Sale/EmailReceiptModal'
import { parseErrorMsg, printHTML } from '../utilities/helpers'
import { LoyaltyModels } from '@getgreenline/loyalties'
import { ReceiptV2 } from '../components/Receipt/ReceiptV2'
import { ReceiptModels } from '@getgreenline/receipts'
import { ReceiptV3 } from '../components/Receipt/ReceiptV3'
import { Button, message } from 'antd-v4'
import { getReceiptPaymentOptionNames } from '../utilities/paymentsHelper'

interface Props {
  onHide: () => void
  payment: IPayment
  currentCompanyStore?: CurrentCompanyStore
  loyaltyData?: LoyaltyModels.ILoyaltyData
  paymentOptionNames?: { [key: number]: string }
  showPaidTag?: boolean
  onReadyToPrint?: () => void
}

interface State {
  showSendEmailModal: boolean
  isSaving: boolean
  receiptSettings?: ReceiptModels.IReceiptContract
  receiptPaymentOptionNames?: { [key: number]: string }
}

@inject('currentCompanyStore')
@observer
export class CustomerReceiptModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      showSendEmailModal: false,
      isSaving: false,
    }
  }

  get company() {
    const company = this.props.currentCompanyStore?.company
    if (!company) throw new Error('Company not found')

    return company
  }

  async componentDidMount() {
    await this.fetchReceiptData()

    if (
      this.state.receiptSettings &&
      this.state.receiptPaymentOptionNames &&
      this.props.onReadyToPrint
    ) {
      this.props.onReadyToPrint()
    }
  }

  setReceiptSettings = () => {
    const { currentCompanyStore } = this.props
    if (!currentCompanyStore) throw new Error('Current company store not found')

    const receiptSettings = currentCompanyStore.getReceiptSettings(
      this.company,
      this.props.payment.locationId,
    )

    return this.setState({ receiptSettings })
  }

  setPaymentOptionNames = async () => {
    const { currentCompanyStore } = this.props
    if (!currentCompanyStore) throw new Error('Current company store not found')

    const receiptPaymentOptionNames = await getReceiptPaymentOptionNames({
      companyId: this.company.id,
      locationId: this.props.payment.locationId,
      payment: this.props.payment,
      paymentOptionNames: this.props.paymentOptionNames,
    })

    return this.setState({ receiptPaymentOptionNames })
  }

  fetchReceiptData = async () => {
    const { currentCompanyStore } = this.props
    if (!currentCompanyStore) throw new Error('Current company store not found')

    try {
      await currentCompanyStore.getCanUseLoyaltyService()

      await this.setReceiptSettings()

      await this.setPaymentOptionNames()

      return
    } catch (error) {
      console.error('Error fetching receipt data', error)
      message.error(parseErrorMsg(error) || 'There was an error fetching receipt data')
    }
  }

  printReceipt() {
    printHTML('print-section', this.company.name, {
      qrCodeCanvasId: 'qr-code',
      qrCodeUrl: this.state.receiptSettings?.loyaltyWalletUrl ?? undefined,
    })
  }

  render() {
    const { onHide, payment, loyaltyData, showPaidTag } = this.props
    const { receiptSettings, receiptPaymentOptionNames } = this.state
    const company = this.company

    if (!receiptSettings) return null

    const loyaltyWalletUrl = this.state.receiptSettings?.loyaltyWalletUrl ?? undefined
    const shouldShowLoyaltyData = company.permissions.canAccessLoyalty

    return (
      <Modal isOpen={true} toggle={() => this.props.onHide()} autoFocus={false}>
        <ModalHeader toggle={() => this.props.onHide()}>Sale</ModalHeader>
        <ModalBody>
          {this.props.currentCompanyStore!.canUseLoyaltyService ? (
            <ReceiptV3
              company={company}
              payment={payment}
              receiptSettings={receiptSettings}
              loyaltyData={shouldShowLoyaltyData ? loyaltyData : undefined}
              loyaltyWalletUrl={
                this.state.receiptSettings?.showLoyaltyWallet ? loyaltyWalletUrl : undefined
              }
              paymentOptionNames={receiptPaymentOptionNames}
              showPaidTag={showPaidTag}
            />
          ) : (
            <ReceiptV2
              company={company}
              payment={payment}
              receiptSettings={receiptSettings}
              loyaltyData={shouldShowLoyaltyData ? loyaltyData : undefined}
              paymentOptionNames={receiptPaymentOptionNames}
              showPaidTag={showPaidTag}
            />
          )}
          <div className='text-right'>
            <Button className='mr-3' onClick={() => onHide()}>
              Close
            </Button>
            <Button
              type='primary'
              className='mr-3'
              onClick={() => this.setState({ showSendEmailModal: true })}
            >
              Email
            </Button>
            <Button type='primary' onClick={() => this.printReceipt()}>
              Print
            </Button>
          </div>
        </ModalBody>
        {this.state.showSendEmailModal && (
          <EmailReceiptModal
            paymentId={payment.id}
            onHide={() => this.setState({ showSendEmailModal: false })}
            paymentOptionNames={receiptPaymentOptionNames}
          />
        )}
      </Modal>
    )
  }
}
